"use client"

import { Fragment } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"

type Variant = "hero" | "footer" | "pricing-footnote"
type CrossBorderKey = "hero" | "footer" | "pricingFootnote"

const STYLES: Record<Variant, string> = {
  hero: "text-base sm:text-lg text-aubergine-700",
  footer: "text-xs text-neutral-500",
  "pricing-footnote": "text-xs text-neutral-600 italic",
}

const KEYS: Record<Variant, CrossBorderKey> = {
  hero: "hero",
  footer: "footer",
  "pricing-footnote": "pricingFootnote",
}

export function CrossBorderNotice({
  variant,
  className,
}: {
  variant: Variant
  className?: string
}) {
  const t = useTranslations("crossBorder")
  const text = t(KEYS[variant])
  // Regulatory micro-copy: wrap only at the "·" seam, never mid-phrase or
  // mid-word. Each "·"-separated segment is kept whole (whitespace-nowrap),
  // so a narrow column breaks exactly at the separator. hyphens:none also
  // neutralises the U+00AD the German soft-hyphen pass injects into
  // "Partner­kliniken" (the global `[lang|="de"] p { hyphens: manual }` rule
  // would otherwise split it as "Partner-\nkliniken"); inline style beats
  // that selector's specificity, both props needed for Safari. Strings
  // without a "·" (e.g. the hero variant) wrap normally.
  const segments = text.split(" · ")
  return (
    <p
      className={cn(STYLES[variant], className)}
      style={{ hyphens: "none", WebkitHyphens: "none" }}
    >
      {segments.length > 1
        ? segments.map((seg, i) => (
            <Fragment key={i}>
              {i > 0 && " · "}
              <span className="whitespace-nowrap">{seg}</span>
            </Fragment>
          ))
        : text}
    </p>
  )
}
